import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

export default function JoinScreen() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const [state, setState] = useState<'loading' | 'ok' | 'invalid'>('loading');
  const [fullName, setFullName] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    authApi
      .invite(token)
      .then((p) => {
        if (p.fullName) setFullName(p.fullName);
        setState('ok');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await authApi.redeem(token, fullName.trim(), login.trim(), password);
      await qc.invalidateQueries({ queryKey: ['me'] });
      // /join не открывается как дип-линк-фолбэк (только напрямую) — pathname тут
      // всегда '/join', так что это шлёт нового пользователя на главную.
      const { pathname } = location;
      const dest = pathname === '/login' || pathname === '/join' ? '/' : pathname;
      nav(dest, { replace: true });
    } catch (e) {
      const m = (e as Error).message;
      setErr(
        m === 'login_taken' ? 'Такой логин уже занят — выберите другой'
        : m === 'invite_invalid' ? 'Ссылка недействительна или устарела'
        : 'Не удалось завершить регистрацию',
      );
    } finally {
      setBusy(false);
    }
  }

  if (state === 'loading') return <div className="p-6 text-tg-hint">Проверяем приглашение…</div>;
  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-tg-bg">
        <Card className="w-full max-w-sm text-center space-y-2">
          <div className="text-lg">🔗 Ссылка недействительна</div>
          <div className="text-sm text-tg-hint">Попросите у администратора новую ссылку-приглашение.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-tg-bg">
      <Card className="w-full max-w-sm space-y-4">
        <h1 className="text-xl font-semibold text-center">Регистрация</h1>
        <form onSubmit={submit} className="space-y-3">
          <input
            className="w-full rounded-lg border border-tg-secondaryBg bg-tg-bg px-3 py-2"
            placeholder="Ваше имя (ФИО)"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <input
            className="w-full rounded-lg border border-tg-secondaryBg bg-tg-bg px-3 py-2"
            placeholder="Логин (email или телефон)"
            autoComplete="username"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />
          <input
            type="password"
            className="w-full rounded-lg border border-tg-secondaryBg bg-tg-bg px-3 py-2"
            placeholder="Пароль (мин. 6 символов)"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err && <div className="text-sm text-red-500">{err}</div>}
          <Button
            type="submit"
            disabled={busy || fullName.trim().length < 2 || login.trim().length < 3 || password.length < 6}
            className="w-full"
          >
            {busy ? 'Создаём…' : 'Войти'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
